/**
 * FreshnessIndicator — surfaces the freshness state of a live-data view.
 *
 * Roadmap Invariant 1: every live-data surface must render a freshness
 * timestamp + a stale-state badge. When the underlying source has not
 * refreshed within its expected window, the data is labeled "Last Known
 * Good" and the staleness is made explicit. The customer is never lied
 * to by omission.
 *
 * Three logical states:
 *   - live     — fresh (within expectedRefreshMs) or actively streaming
 *   - stale    — past expected refresh window but within graceMs; data
 *                still shown but labeled "Last Known Good"
 *   - offline  — past staleMs OR explicit connection failure; data is
 *                labeled "Last Known Good" with strong warning styling
 */
import { useEffect, useState } from 'react';
import { cn } from '../lib/utils';

export type FreshnessState = 'live' | 'stale' | 'offline' | 'unknown';

export interface FreshnessIndicatorProps {
  /** ISO timestamp of last successful refresh / data update. null = never */
  lastUpdatedAt: string | null | undefined;
  /** Beyond this many ms with no refresh, the indicator goes "stale". Default 5min. */
  expectedRefreshMs?: number;
  /** Beyond this many ms, the indicator goes "offline" (Last Known Good). Default 15min. */
  staleMs?: number;
  /** Optional explicit state override — useful when you know the connection is dead (e.g. Realtime CHANNEL_ERROR). */
  forcedState?: FreshnessState;
  /** Label prefix (e.g. "Alerts", "Devices"). Defaults to "Data". */
  label?: string;
  /** Whether the data is currently being streamed (Realtime subscribed). */
  streaming?: boolean;
  /**
   * Short form for tight containers: the dot, one state word and the age.
   *
   * The full chip reads "Offline — Last Known Good · UniFi 5h ago", which needs
   * roughly 250px. Inside a quarter-width KPI card it wrapped onto four lines,
   * and because the chip is `rounded-full` a multi-line box renders as an
   * ellipse with the text spilling out of it — the state was unreadable exactly
   * where it matters most. Compact keeps it on one line; the full wording stays
   * in the `title` and in the screen-reader announcement, so nothing is lost.
   */
  compact?: boolean;
  /**
   * Translated wording, per state. Supplying it is how this component gets
   * translated at all.
   *
   * The chip lives in the shared package, which has no i18n provider because it
   * serves four apps with different bundles — so its labels were English
   * constants and stayed English on a Spanish page. That made the one surface
   * carrying this platform's cardinal invariant the one surface a Spanish-
   * speaking customer could not read. The customer portal passes translations
   * through `<Freshness>`; the staff apps keep the English defaults.
   */
  stateLabels?: Partial<Record<FreshnessState, string>>;
  /** Short forms for `compact`. Falls back to `stateLabels`, then English. */
  shortStateLabels?: Partial<Record<FreshnessState, string>>;
  /** Replaces "Live · streaming" when a live view is actively streaming. */
  streamingLabel?: string;
  /**
   * Renders the age of the last reading. Supply it to translate the age, which
   * the built-in formatter emits in English ("5h ago", "just now", "never").
   *
   * A function rather than a bag of strings because the wording depends on the
   * magnitude — a translation may pluralise or reorder, and "hace 5h" puts its
   * preposition before the number where English puts "ago" after it.
   */
  formatAge?: (lastUpdatedAt: string | null | undefined) => string;
  className?: string;
}

function computeState(
  lastUpdatedAt: string | null | undefined,
  expectedRefreshMs: number,
  staleMs: number,
  forcedState: FreshnessState | undefined,
): FreshnessState {
  if (forcedState) return forcedState;
  if (!lastUpdatedAt) return 'unknown';
  const ageMs = Date.now() - new Date(lastUpdatedAt).getTime();
  if (Number.isNaN(ageMs)) return 'unknown';
  if (ageMs > staleMs) return 'offline';
  if (ageMs > expectedRefreshMs) return 'stale';
  return 'live';
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'unknown';
  if (ms < 0) return 'just now';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const stateStyles: Record<
  FreshnessState,
  { dot: string; chip: string; label: string; short: string }
> = {
  live: {
    dot: 'bg-success animate-pulse',
    chip: 'bg-success-soft text-success border-success/20',
    label: 'Live',
    short: 'Live',
  },
  stale: {
    dot: 'bg-warning',
    chip: 'bg-warning-soft text-warning border-warning/20',
    label: 'Stale — Last Known Good',
    // "Stale" alone still says do not trust this as current, which is the
    // whole job of the chip. The full wording is one hover away.
    short: 'Stale',
  },
  offline: {
    dot: 'bg-error',
    chip: 'bg-error-soft text-error border-error/20',
    label: 'Offline — Last Known Good',
    short: 'Offline',
  },
  unknown: {
    dot: 'bg-text-faint',
    chip: 'bg-surface-3 text-text-muted border-border',
    label: 'No data yet',
    short: 'No data',
  },
};

export function FreshnessIndicator({
  lastUpdatedAt,
  expectedRefreshMs = 5 * 60 * 1000,
  staleMs = 15 * 60 * 1000,
  forcedState,
  label = 'Data',
  streaming,
  compact = false,
  stateLabels,
  shortStateLabels,
  streamingLabel,
  formatAge,
  className,
}: FreshnessIndicatorProps) {
  const [, forceTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const state = computeState(lastUpdatedAt, expectedRefreshMs, staleMs, forcedState);
  const styles = stateStyles[state];
  const fullLabel = stateLabels?.[state] ?? styles.label;
  const stateLabel =
    streaming && state === 'live' ? (streamingLabel ?? 'Live · streaming') : fullLabel;
  // A short label falls back to the full translated one before the English
  // constant, so a caller that translates only `stateLabels` still reads in the
  // right language — merely longer.
  const shownLabel = compact ? (shortStateLabels?.[state] ?? stateLabels?.[state] ?? styles.short) : stateLabel;
  const age = (formatAge ?? relativeTime)(lastUpdatedAt);

  // The live region's text depends only on the freshness STATE (and label), so
  // React never touches that text node on the 30s relative-time tick and screen
  // readers are only interrupted when live → stale → offline actually changes.
  // Always the FULL wording, never the compact form: a screen reader has no
  // hover to fall back on, so the short label must not reach it.
  const announcement = `${label}: ${stateLabel}`;

  return (
    <div
      className={cn(
        // `whitespace-nowrap` is load-bearing, not cosmetic: `rounded-full` on a
        // box that has wrapped to two lines draws an ellipse, so the chip must
        // never wrap. `max-w-full` plus the truncation below keeps it inside a
        // narrow parent instead of pushing the layout sideways.
        'inline-flex max-w-full items-center gap-2 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs',
        styles.chip,
        className,
      )}
      title={
        lastUpdatedAt
          ? `${label} last refreshed ${new Date(lastUpdatedAt).toLocaleString()}`
          : `${label} has never refreshed`
      }
    >
      <span className={cn('h-2 w-2 shrink-0 rounded-full', styles.dot)} aria-hidden="true" />
      <span className="shrink-0 font-medium">{shownLabel}</span>
      <span className="shrink-0 opacity-70" aria-hidden="true">
        ·
      </span>
      {/* The age is the part that may be clipped: losing "UniFi" is survivable,
          losing the state word is not, so only this span shrinks. */}
      <span className="min-w-0 truncate opacity-80">
        {compact ? age : `${label} ${age}`}
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
